// src/components/sponsor/interests/SponsorInterests.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchMyInterests } from "@/store/slices/sponsorSlice";
import { Heart, Calendar, CheckCircle } from "lucide-react";
import Badge from "@/components/common/Badge";
import Loading from "@/components/common/Loading";
import {
  getInterestStatusBadgeConfig,
  getComponentLabel,
} from "@/utils/constants";
import EventFilters from "@/components/common/EventFilters";
import useFilterParams from "@/hooks/useFilterParams";

const DEFAULT_FILTERS = {
  search: "",
  event_type: "",
  component: "",
  city: "",
  startDate: "",
  endDate: "",
  sort: "recent",
  status: ""
};

const INTEREST_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "accepted", label: "Accepted" },
  { value: "partially_accepted", label: "Partially Accepted" },
  { value: "declined", label: "Declined" },
];

const SponsorInterests = () => {
  const dispatch = useDispatch();
  const { interests, isLoading, sponsorComponentCounts, interestsPagination } = useSelector((state) => state.sponsor);
  
  const [filters, handleFilterChange] = useFilterParams(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    const params = { page, limit, ...filters };
    
    // Remove empty strings
    Object.keys(params).forEach(key => {
      if (params[key] === "") {
        delete params[key];
      }
    });
    
    dispatch(fetchMyInterests(params));
  }, [dispatch, filters, page, limit]);

  const getStatusBadge = (status) => {
    const c = getInterestStatusBadgeConfig(status);
    return (
      <Badge tone={c.tone} style={c.customStyle}>
        <span className="flex items-center gap-1">{c.label}</span>
      </Badge>
    );
  };

  const hasFilters = Object.entries(filters).some(([key, value]) => key !== 'sort' && value !== '');

  if (isLoading && interests.length === 0) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          My Interests
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Track your sponsorship interest submissions
        </p>
      </div>

      {/* Filters */}
      <EventFilters
        filters={filters}
        onChange={handleFilterChange}
        componentCounts={sponsorComponentCounts}
        showStatus={true}
        statusOptions={INTEREST_STATUS_OPTIONS}
      />

      {/* Interests List */}
      {interests.length === 0 && !hasFilters ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <Heart className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
            No Interests Yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            Browse available events and express interest in sponsorship opportunities.
          </p>
        </div>
      ) : interests.length === 0 && hasFilters ? (
        <div className="py-12 text-center">
          <Heart className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No interests match your filters.</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {interests.map((interest, index) => {
              const event = interest.event || interest.event_id;
            return (
              <motion.div
                key={interest._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={event?._id ? `/sponsor/events/${event._id}` : "#"}
                  className="block bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-lg group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                          {event?.title || "Event"}
                        </h3>
                        {getStatusBadge(interest.status)}
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {event?.start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(event.start_date).toLocaleDateString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </span>
                        )}
                        {interest.total_value && (
                          <span className="font-medium text-teal-600 dark:text-teal-400">
                            Total: ₹{interest.total_value.toLocaleString()}
                          </span>
                        )}
                      </div>

                      {/* Interested Components */}
                      {interest.interested_components?.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                            Interested Components
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {interest.interested_components.map((categoryEnum) => {
                              // Find component details from event object
                              const component =
                                event?.sponsorship_components?.find(
                                  (c) => c.category === categoryEnum
                                );
                              return (
                                <Badge
                                  key={categoryEnum}
                                  tone="info"
                                  size="sm"
                                  className="bg-teal-50 text-teal-700 border-teal-200"
                                >
                                  {getComponentLabel(component?.category) || "Unknown Component"}
                                  {component?.type === "kind" ? (
                                    <span className="ml-1 text-xs opacity-70 font-normal">
                                      (In-Kind)
                                    </span>
                                  ) : component?.value && (
                                    <span className="ml-1 opacity-70">
                                      (₹{Number(component.value).toLocaleString()}
                                      )
                                    </span>
                                  )}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {interest.message && (
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-100 dark:border-gray-800">
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Your Message:
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 italic">
                            "{interest.message}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Admin Response (if any) */}
                  {interest.admin_notes && (
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 bg-amber-50/50 dark:bg-amber-900/10 -mx-6 -mb-6 px-6 py-4 rounded-b-xl">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Response from Organizer:
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        {interest.admin_notes}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center text-xs text-gray-400">
                    <span>
                      Submitted on{" "}
                      {new Date(interest.created_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span className="group-hover:translate-x-1 transition-transform text-teal-600 dark:text-teal-400 font-medium flex items-center gap-1">
                      View Details &rarr;
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
          </div>

          {interestsPagination && interestsPagination.pages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-lg mt-6 shadow-sm">
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{" "}
                    <span className="font-medium">
                      {(interestsPagination.page - 1) * limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(interestsPagination.page * limit, interestsPagination.total)}
                    </span>{" "}
                    of <span className="font-medium">{interestsPagination.total}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:ring-gray-600 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      &larr;
                    </button>
                    {/* Simplified page numbers for UI */}
                    <button className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600">
                      Page {page} of {interestsPagination.pages}
                    </button>
                    <button
                      onClick={() => setPage(Math.min(interestsPagination.pages, page + 1))}
                      disabled={page === interestsPagination.pages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:ring-gray-600 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      &rarr;
                    </button>
                  </nav>
                </div>
              </div>
              
              {/* Mobile pagination */}
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Previous
                </button>
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  {page} / {interestsPagination.pages}
                </div>
                <button
                  onClick={() => setPage(Math.min(interestsPagination.pages, page + 1))}
                  disabled={page === interestsPagination.pages}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SponsorInterests;
